import React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { useUIStore } from '@/stores/useUIStore';

/**
 * LoadingOverlay — Full-screen overlay with frosted-glass backdrop,
 * circular progress indicator, and status messaging.
 *
 * Controlled entirely by the `useUIStore` loading slice:
 *   showLoading("Computing…")  → shows overlay
 *   updateProgress(42, "Step 2/5")
 *   hideLoading()               → hides overlay
 */
const LoadingOverlay: React.FC = () => {
  const { active, progress, message } = useUIStore((s) => s.loading);

  if (!active) return null;

  const isDeterminate = progress > 0;

  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
        backdropFilter: 'blur(6px)',
        bgcolor: 'rgba(30, 27, 24, 0.72)',
      }}
    >
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            px: 7,
            py: 6,
            borderRadius: 4,
            bgcolor: 'rgba(255, 255, 255, 0.07)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4)',
            width: { xs: '90vw', sm: 480 },
            maxWidth: 480,
            textAlign: 'center',
          }}
        >
          {/* Circular indicator with optional percentage */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {/* Track ring — always renders a full circle as the background */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={96}
              thickness={4.5}
              sx={{
                color: 'rgba(255, 255, 255, 0.12)',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            <CircularProgress
              variant={isDeterminate ? 'determinate' : 'indeterminate'}
              value={progress}
              size={96}
              thickness={4.5}
              sx={{
                color: '#ff7f0e',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            {isDeterminate && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '1.1rem' }}
                >
                  {Math.round(progress)}%
                </Typography>
              </Box>
            )}
          </Box>

          {/* Status message */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.5,
              }}
            >
              {message || 'Computing…'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: 'rgba(255, 255, 255, 0.6)',
                fontWeight: 400,
                fontSize: '0.9rem',
              }}
            >
              Please do not refresh or close this page.
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );
};

export default LoadingOverlay;
